import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useWebSocketContext } from '@/components/WebSocketProvider'
import { liveStatusView } from '@/lib/liveLink'
import { cn } from '@/lib/utils'

// #328 — after this long without a live connection the user is told once; again once when it is back
export const LIVE_PAUSED_AFTER_MS = 10_000
const PAUSED_TOAST_ID = 'live-paused'
const BACK_TOAST_ID = 'live-back'

const DOT_COLOUR = {
  live: 'bg-green-500',
  reconnecting: 'bg-amber-500 animate-pulse',
  offline: 'bg-gray-400',
} as const

/** #328 — the live status as a 10 px dot: green live, amber pulsing reconnecting, grey off. The header shows it
 *  on phones, where the pill (WebSocketStatus) is hidden; both read the same status with the same words. */
export function LiveIndicator() {
  const { t } = useTranslation('common')
  const { status } = useWebSocketContext()
  const view = liveStatusView(status)
  const label = t(`live.${view}`)
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full', DOT_COLOUR[view])}
    />
  )
}

/** #328 — one toast after 10 s reconnecting or offline, one when live again; one of each per outage. The first
 *  connection of the page (connecting) does not count. Called once, by the header. */
export function useLiveStatusToasts() {
  const { t } = useTranslation('common')
  const tRef = useRef(t)
  useEffect(() => { tRef.current = t }, [t])
  const { status } = useWebSocketContext()
  const down = status === 'reconnecting' || status === 'offline'
  const live = status === 'live'
  const pausedShownRef = useRef(false)

  useEffect(() => {
    if (!down || pausedShownRef.current) return
    const timer = setTimeout(() => {
      pausedShownRef.current = true
      toast.warning(tRef.current('live.paused'), { id: PAUSED_TOAST_ID })
    }, LIVE_PAUSED_AFTER_MS)
    return () => clearTimeout(timer)
  }, [down])

  useEffect(() => {
    if (!live || !pausedShownRef.current) return
    pausedShownRef.current = false
    toast.dismiss(PAUSED_TOAST_ID)
    toast.success(tRef.current('live.back'), { id: BACK_TOAST_ID })
  }, [live])
}
